import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createCategory,
  deleteCategory,
  getCategories,
  updateCategory,
} from "../../../services/category";
import toast from "react-hot-toast";

function useCategory() {
  const queryClient = useQueryClient();

  // Get all categories query
  const {
    data: categories = [],
    isLoading: isGettingCategories,
    error: errorGettingCategories,
  } = useQuery({
    queryKey: ["categories"],
    queryFn: () => getCategories(),
  });

  // Create category mutation
  const { mutate: createCategoryFn, isLoading: isCreatingCategory } =
    useMutation({
      mutationFn: (category) => createCategory(category),
      onSuccess: () => {
        queryClient.invalidateQueries(["categories"]);
        toast.success("Category created successfully");
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.error?.slice(0, 5) === "11000"
            ? "Category already exists"
            : error.response?.data?.error || "Something went wrong"
        );
      },
    });

  // Delete category mutation
  const { mutate: deleteCategoryFn, isLoading: isDeletingCategory } =
    useMutation({
      mutationFn: (categoryId) => deleteCategory(categoryId),
      onSuccess: () => {
        queryClient.invalidateQueries(["categories"]);
        toast.success("Category deleted successfully");
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || "Error deleting category");
      },
    });

  // Update category mutation
  const { mutate: updateCategoryFn, isLoading: isUpdatingCategory } =
    useMutation({
      mutationFn: ({ categoryId, category }) =>
        updateCategory(categoryId, category),
      onSuccess: () => {
        queryClient.invalidateQueries(["categories"]);
        toast.success("Category updated successfully");
      },
      onError: (error) => {
        toast.error(
          error.response?.data?.error?.slice(0, 5) === "11000"
            ? "Category already exists"
            : error.response?.data?.error || "Something went wrong"
        );
      },
    });

  return {
    // GET CATEGORIES
    categories,
    isGettingCategories,
    errorGettingCategories:
      errorGettingCategories?.response?.data?.error ||
      errorGettingCategories?.message,

    // CREATE CATEGORY
    createCategoryFn,
    isCreatingCategory,

    // UPDATE CATEGORY
    updateCategoryFn,
    isUpdatingCategory,

    // DELETE CATEGORY
    deleteCategoryFn,
    isDeletingCategory,
  };
}

export default useCategory;
